<template>
    <div class="calendar-header-day">
        <div class="day-label-group">
            <button-tooltip :tooltip="dayStatusTooltip" :disabled="dragging || !dayStatusTooltip">
                <template v-slot:activator="{ on, attrs }">
                    <span class="calendar-day" :class="dayStatusClass" v-bind="attrs" v-on="on"
                        @mousedown.left="onDayMouseDown" @click="$emit('click:day')">{{ day }}</span>
                </template>
            </button-tooltip>

            <!-- WELLNESS -->
            <button-tooltip v-if="showWellnessBadge" variant="wellness" :disabled="dragging">
                <template v-slot:activator="{ on, attrs }">
                    <mini-badge type="wellness" :level="wellnessBadgeLevel" v-bind="attrs" v-on="on">
                        {{ wellnessValue }}
                    </mini-badge>
                </template>
                <template v-slot:tooltip>
                    <wellness-summary :pre-info="wellnessPreInfo" :date="wellnessDate" />
                </template>
            </button-tooltip>

            <!-- RPE -->
            <button-tooltip v-if="showRpeBadge" :tooltip="rpeTooltip" :disabled="dragging">
                <template v-slot:activator="{ on, attrs }">
                    <mini-badge type="rpe" :level="rpeBadgeLevel" v-bind="attrs" v-on="on">
                        {{ rpeValue }}
                    </mini-badge>
                </template>
            </button-tooltip>

            <match-day-badge v-if="matchDay" :value="matchDay" />

            <span class="day-name" v-if="dayLabel">{{ dayLabel }}</span>
        </div>
        <div class="day-actions" v-if="isCurrentMonth">
            <!-- NOTE -->
            <button-tooltip icon x-small @click="$emit('click:note')" :disabled="dragging"
                :color="hasNote ? 'secondary' : 'txtMuted'">
                <v-icon size="16">{{ hasNote ? 'mdi-note-edit' : 'mdi-note-plus' }}</v-icon>
                <template v-slot:tooltip>
                    <span v-if="hasNote" v-html="formatNote()" />
                    <span v-else>{{ $t('tooltips.add_note') }}</span>
                </template>
            </button-tooltip>

            <!-- COMPETENCE / QUESTIONNAIRE -->
            <template v-if="!isDayView">
                <button-tooltip v-if="!hasEventsToday" @click="$emit('click:add:competence')" icon color="txtMuted"
                    x-small :tooltip="$t('tooltips.add_competence')" :disabled="dragging">
                    <v-icon size="16">mdi-soccer-field</v-icon>
                </button-tooltip>
                <button-tooltip v-else @click="$emit('click:assign:questionnaire')" icon :color="colorQuestionnaire"
                    x-small :tooltip="questionnaireTooltip" :disabled="dragging">
                    <v-icon size="16">mdi-help-box</v-icon>
                </button-tooltip>
            </template>

            <!-- ADD / MENU -->
            <button-tooltip v-if="!hasEventsToday && !isDayView" @click="$emit('click:add')" icon color="txtMuted"
                x-small :tooltip="$t('tooltips.add_exercise')" :disabled="dragging">
                <v-icon size="16">$add_circle</v-icon>
            </button-tooltip>
            <v-menu v-else-if="!isCompetenceDay" bottom left offset-y open-on-hover :disabled="dragging">
                <template v-slot:activator="{ on, attrs }">
                    <v-btn icon x-small v-bind="attrs" v-on="on" color="txtMuted">
                        <v-icon size="16">mdi-dots-vertical</v-icon>
                    </v-btn>
                </template>

                <v-list dense>
                    <template v-for="(menu, i) in menuOptions">
                        <v-divider v-if="menu.divider" :key="`divider-${i}`" class="my-1" />
                        <v-list-item :key="i" @click="$emit(`click:${menu.value}`)">
                            <v-list-item-title>
                                <v-icon size="17" :color="menu.danger ? 'error' : 'txtSecondary'">{{ menu.icon }}</v-icon>
                                <span class="menu-list-text" :class="{ 'error--text': menu.danger }">{{ menu.title }}</span>
                            </v-list-item-title>
                        </v-list-item>
                    </template>
                </v-list>
            </v-menu>
        </div>
    </div>
</template>

<script>
import ButtonTooltip from "@/components/common/ButtonTooltip";
import WellnessSummary from "@/components/common/WellnessSummary";
import MiniBadge from "@/components/common/MiniBadge";
import MatchDayBadge from "@/components/calendar/MatchDayBadge";
import { nl2br } from "@/utils/Common";
import { calculateWellnessPreInfo, getDayStatus, rpeLevel, sumWellnessPreInfo, wellnessLevel } from "@/utils/Routine";

export default {
    name: "DayLabelCalendar",
    components: { ButtonTooltip, WellnessSummary, MiniBadge, MatchDayBadge },
    props: {
        day: {
            type: [Number, String],
            default: 0
        },
        isCurrentMonth: {
            type: Boolean,
            default: false
        },
        hasEventsToday: {
            type: Boolean,
            default: false
        },
        canLockDay: {
            type: Boolean,
            default: false
        },
        note: {
            type: Object,
            default: null
        },
        isCompetenceDay: {
            type: Boolean,
            default: false
        },
        isDayView: {
            type: Boolean,
            default: false
        },
        savingContent: {
            type: Boolean,
            default: false
        },
        routine: {
            type: Object,
            default: null
        },
        dragging: {
            type: Boolean,
            default: false
        },
        dayLabel: {
            type: String,
            default: ''
        },
        date: {
            type: String,
            default: ''
        },
        isStudentView: {
            type: Boolean,
            default: false
        },
        matchDay: {
            type: String,
            default: null
        }
    },
    computed: {
        menuOptions(vm = this) {
            return [
                { title: vm.$t('calendar.change_name'), icon: 'mdi-clipboard-edit-outline', value: 'save:name' },
                { title: vm.$t('calendar.copy_routine'), icon: '$copy', value: 'copy' },
                { title: vm.$t('calendar.copy_week'), icon: '$copy', value: 'copy:week' },
                { title: vm.$t('calendar.save_routine'), icon: '$save', value: 'save:routine' },
                { title: vm.$t('calendar.save_program'), icon: '$save', value: 'save:program' },
                { title: vm.$t('calendar.save_block'), icon: '$save', value: 'save:block' },
                { title: vm.$t('calendar.delete_routine'), icon: '$delete', value: 'delete', divider: true, danger: true },
            ]
        },
        hasNote(vm = this) {
            return !!vm.note?.message
        },
        hasQuestionnaire(vm = this) {
            return vm.routine?.assignedQuestionnaireId || false
        },
        hasQuestionnaireCompleted(vm = this) {
            return !!vm.hasQuestionnaire && vm.routine?.advance?.pre === true
        },
        questionnaireTooltip(vm = this) {
            if (vm.hasQuestionnaireCompleted) return vm.$t('tooltips.completed_questionnaire')
            return vm.hasQuestionnaire ? vm.$t('tooltips.edit_questionnaire') : vm.$t('tooltips.assign_questionnaire')
        },
        colorQuestionnaire(vm = this) {
            if (vm.hasQuestionnaireCompleted) return 'secondary'
            return vm.hasQuestionnaire ? 'accent' : 'txtMuted'
        },
        wellnessPreInfo(vm = this) {
            return vm.routine?.advance?.preInfo || null
        },
        hasWellnessInfo(vm = this) {
            return !!vm.wellnessPreInfo
        },
        wellnessDate(vm = this) {
            return vm.routine?.rawDate?.full || vm.date
        },
        showDayInfo(vm = this) {
            return vm.isStudentView && vm.isCurrentMonth
        },
        dayStatus(vm = this) {
            if (!vm.showDayInfo || !vm.date) return 'none'
            return getDayStatus(vm.routine, vm.$moment(vm.date), vm.$moment())
        },
        dayStatusClass(vm = this) {
            if (vm.dayStatus === 'done') return 'day-done'
            return vm.dayStatus === 'not_realize' ? 'day-missed' : ''
        },
        dayStatusTooltip(vm = this) {
            if (vm.dayStatus === 'done') return vm.$t('tooltips.routine_completed')
            return vm.dayStatus === 'not_realize' ? vm.$t('tooltips.routine_not_completed') : ''
        },
        showWellnessBadge(vm = this) {
            return vm.showDayInfo && vm.hasWellnessInfo
        },
        wellnessValue(vm = this) {
            return calculateWellnessPreInfo(vm.wellnessPreInfo, 1)
        },
        wellnessBadgeLevel(vm = this) {
            return wellnessLevel(sumWellnessPreInfo(vm.wellnessPreInfo))
        },
        rpeValue(vm = this) {
            const effort = vm.routine?.advance?.postInfo?.effort
            if (effort === null || effort === undefined || effort === '') return null
            const value = Number(effort)
            return isNaN(value) ? null : value
        },
        showRpeBadge(vm = this) {
            return vm.showDayInfo && vm.routine?.advance?.post === true && vm.rpeValue !== null
        },
        rpeBadgeLevel(vm = this) {
            return rpeLevel(vm.rpeValue)
        },
        rpeTooltip(vm = this) {
            return vm.$t('tooltips.session_rpe', { value: vm.rpeValue })
        }
    },
    methods: {
        onDayMouseDown(event) {
            const vm = this
            if (!vm.hasEventsToday || vm.isDayView) return
            const start = { x: event.clientX, y: event.clientY }
            const move = (e) => {
                if (Math.abs(e.clientX - start.x) < 5 && Math.abs(e.clientY - start.y) < 5) return
                cleanup()
                vm.$emit('drag:day', e)
            }
            const cleanup = () => {
                window.removeEventListener('mousemove', move)
                window.removeEventListener('mouseup', cleanup)
            }
            window.addEventListener('mousemove', move)
            window.addEventListener('mouseup', cleanup)
        },
        formatNote() {
            const vm = this
            return nl2br(vm.note?.message || '')
        }
    }
}
</script>

<style scoped>
.calendar-header-day {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 4px;
    min-height: 32px;
    padding: 2px 4px 2px 8px;
}

.day-label-group {
    display: flex;
    align-items: center;
    gap: 5px;
    min-width: 0;
}

.day-name {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: .05em;
    color: var(--v-txtMuted-base);
    white-space: nowrap;
    margin: auto;
}

.calendar-day {
    font-weight: 700;
    font-size: 15px;
    line-height: 20px;
    color: var(--v-txtPrimary-base);
    cursor: pointer;
}

.theme--dark .calendar-day:not(.day-done):not(.day-missed):hover {
    color: var(--v-primary-base);
}

.theme--light .calendar-day:not(.day-done):not(.day-missed):hover {
    color: var(--v-accent-base);
}

.calendar-day.day-done,
.calendar-day.day-missed {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    font-weight: 800;
    line-height: 1;
}

.calendar-day.day-done::after,
.calendar-day.day-missed::after {
    position: absolute;
    bottom: -3px;
    right: -3px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 13px;
    height: 13px;
    border-radius: 50%;
    border: 2px solid var(--day-status-ring, var(--v-bg-base));
    font-size: 8px;
    font-weight: 900;
    line-height: 1;
}

.calendar-day.day-done {
    color: #0C0E12;
    background: var(--v-primary-base);
    box-shadow: 0 0 0 3px rgba(204, 255, 51, 0.25);
}

.calendar-day.day-done::after {
    content: '\2713';
    background: var(--v-primary-base);
    color: #0C0E12;
}

.theme--light .calendar-day.day-done {
    color: #FFFFFF;
    background: var(--v-accent-base);
    box-shadow: 0 0 0 3px rgba(51, 153, 153, 0.25);
}

.theme--light .calendar-day.day-done::after {
    background: var(--v-accent-base);
    color: #FFFFFF;
}

.calendar-day.day-missed {
    color: #FFFFFF;
    background: var(--v-error-base);
    box-shadow: 0 0 0 3px rgba(232, 92, 92, 0.25);
}

.calendar-day.day-missed::after {
    content: '\2715';
    background: var(--v-error-base);
    color: #FFFFFF;
}

.day-actions {
    display: flex;
    align-items: center;
    gap: 0;
}

.menu-list-text {
    color: var(--v-txtSecondary-base);
    font-weight: 400;
    font-size: 13px;
    line-height: 12px;
    margin-left: 8px;
}
</style>